import { MonthlyPlan, MonthlyPlanActivity } from '../apiServices/types';

export const MONTH_NAMES = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'
];

const MONEY_EPSILON = 0.005;

export interface PlanPeriod {
  year: number;
  month: number; // 1-12
}

export interface MonthWeek {
  index: number;
  startDay: number;
  endDay: number;
  days: number;
  startKey: string; // YYYY-MM-DD
  endKey: string;
}

export interface PlanFixedExpenseInput {
  key: string;
  amount: number;
  paymentsCount: number;
  dayOfMonth?: number | null;
  recurringExpenseId?: string | null;
  categoryId?: string | null;
}

export interface PlanSavingsGoalInput {
  key: string;
  accountId: string;
  amount: number;
}

export interface PlanInput extends PlanPeriod {
  incomes: { amount: number }[];
  fixedExpenses: PlanFixedExpenseInput[];
  savingsGoals: PlanSavingsGoalInput[];
}

export type FixedExpenseStatus = 'paid' | 'partial' | 'pending' | 'overdue';

export interface PlanPayment {
  date: string;
  amount: number;
  receiptId?: string;
  receiptName?: string;
}

export interface FixedExpenseProgress {
  key: string;
  planned: number;
  actual: number;
  /** Template items: number of linked receipts; null for category-tracked items */
  paymentsMade: number | null;
  expectedPayments: number | null;
  status: FixedExpenseStatus;
  overAmount: number;
  dueDateKey: string | null;
  isTracked: boolean;
  payments: PlanPayment[];
}

export interface SavingsGoalProgress {
  key: string;
  planned: number;
  actual: number;
  remaining: number;
  progress: number;
}

export type WeekStatus = 'past' | 'current' | 'future';

export interface PlanWeek extends MonthWeek {
  status: WeekStatus;
  /** Even share of the free money by days */
  baseBudget: number;
  /** Share after carrying over the results of previous weeks */
  budget: number;
  carryOver: number;
  spent: number;
  /** Money released back to the budget (fixed expenses paid below the plan, savings withdrawals over the plan) */
  returned: number;
  left: number;
  daysLeft: number;
}

export interface MonthlyPlanSummary {
  totalIncome: number;
  totalFixed: number;
  totalSavings: number;
  freeAmount: number;
  actualIncome: number;
  fixedActual: number;
  savingsActual: number;
  unplannedSavings: number;
  variableSpent: number;
  returned: number;
  freeLeft: number;
  fixedExpenses: FixedExpenseProgress[];
  savingsGoals: SavingsGoalProgress[];
  weeks: PlanWeek[];
  currentWeek: PlanWeek | null;
}

interface ItemEvent extends PlanPayment {
  isPayment: boolean;
}

type DailyAmounts = Map<string, number>;

const pad = (value: number): string => String(value).padStart(2, '0');

const sum = (values: number[]): number => values.reduce((total, value) => total + value, 0);

export const roundMoney = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const makeDateKey = (year: number, month: number, day: number): string => `${year}-${pad(month)}-${pad(day)}`;

export const toDateKey = (value: string): string => value.split('T')[0];

export const getDaysInMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

export const getCurrentPeriod = (today: Date = new Date()): PlanPeriod => ({
  year: today.getFullYear(),
  month: today.getMonth() + 1
});

export const shiftPeriod = (period: PlanPeriod, delta: number): PlanPeriod => {
  const date = new Date(period.year, period.month - 1 + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
};

export const isSamePeriod = (a: PlanPeriod, b: PlanPeriod): boolean => a.year === b.year && a.month === b.month;

export const formatPeriod = (period: PlanPeriod): string => `${MONTH_NAMES[period.month - 1]} ${period.year}`;

export const toPeriodKey = (period: PlanPeriod): string => `${period.year}-${pad(period.month)}`;

export const parsePeriodKey = (value: string | null): PlanPeriod | null => {
  const match = value?.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);

  return month >= 1 && month <= 12 && year >= 2000 && year <= 2100 ? { year, month } : null;
};

const getShortMonthName = (year: number, month: number): string =>
  new Date(year, month - 1, 1).toLocaleDateString('uk-UA', { month: 'short' });

export const formatWeekRange = (week: MonthWeek, period: PlanPeriod): string => {
  const monthName = getShortMonthName(period.year, period.month);
  return week.startDay === week.endDay
    ? `${week.startDay} ${monthName}`
    : `${week.startDay}–${week.endDay} ${monthName}`;
};

export const formatDateKey = (dateKey: string): string => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' });
};

/**
 * Calendar weeks (Monday – Sunday) clipped to the month boundaries
 */
export const getMonthWeeks = (year: number, month: number): MonthWeek[] => {
  const daysInMonth = getDaysInMonth(year, month);
  const weeks: MonthWeek[] = [];
  let startDay = 1;

  while (startDay <= daysInMonth) {
    const dayOfWeek = new Date(year, month - 1, startDay).getDay();
    const daysToSunday = (7 - dayOfWeek) % 7;
    const endDay = Math.min(startDay + daysToSunday, daysInMonth);

    weeks.push({
      index: weeks.length,
      startDay,
      endDay,
      days: endDay - startDay + 1,
      startKey: makeDateKey(year, month, startDay),
      endKey: makeDateKey(year, month, endDay)
    });

    startDay = endDay + 1;
  }

  return weeks;
};

/**
 * Stable sort from the largest amount to the smallest
 */
export const sortByAmountDesc = <T>(items: T[], getAmount: (item: T) => number): T[] =>
  items
    .map((item, index) => ({ item, index, amount: getAmount(item) }))
    .sort((a, b) => b.amount - a.amount || a.index - b.index)
    .map(({ item }) => item);

/**
 * Dashboard order: unpaid items by amount, paid ones go to the bottom as completed
 */
export const sortFixedExpensesForDashboard = (items: FixedExpenseProgress[]): FixedExpenseProgress[] => [
  ...sortByAmountDesc(items.filter(item => item.status !== 'paid'), item => item.planned),
  ...sortByAmountDesc(items.filter(item => item.status === 'paid'), item => item.planned)
];

export const buildPlanInput = (plan: MonthlyPlan): PlanInput => ({
  year: plan.year,
  month: plan.month,
  incomes: plan.incomes.map(income => ({ amount: income.amount })),
  fixedExpenses: plan.fixedExpenses.map(item => ({
    key: item.id,
    amount: item.amount,
    paymentsCount: item.paymentsCount,
    dayOfMonth: item.dayOfMonth,
    recurringExpenseId: item.recurringExpenseId,
    categoryId: item.categoryId
  })),
  savingsGoals: plan.savingsGoals.map(goal => ({
    key: goal.id,
    accountId: goal.accountId,
    amount: goal.amount
  }))
});

const addToDay = (amounts: DailyAmounts, dateKey: string, amount: number): void => {
  amounts.set(dateKey, (amounts.get(dateKey) ?? 0) + amount);
};

const sumWeek = (amounts: DailyAmounts, week: MonthWeek): number => {
  let total = 0;
  amounts.forEach((amount, dateKey) => {
    if (dateKey >= week.startKey && dateKey <= week.endKey) {
      total += amount;
    }
  });
  return total;
};

/**
 * Applies item movements against its planned amount: only the part above the plan reaches the
 * weekly budget (as spending), and money released below it comes back (as returned).
 * Items with expected payments are settled once all payments are made, releasing the unused rest.
 */
const settleItem = (
  planned: number,
  expectedPayments: number | null,
  events: ItemEvent[],
  spentByDay: DailyAmounts,
  returnedByDay: DailyAmounts
): { actual: number; paymentsMade: number; settled: boolean; events: ItemEvent[] } => {
  const sortedEvents = [...events].sort((a, b) => a.date.localeCompare(b.date));
  let actual = 0;
  let paymentsMade = 0;
  let cap = planned;
  let countedOver = 0;
  let settled = false;

  sortedEvents.forEach(event => {
    actual += event.amount;
    if (event.isPayment) {
      paymentsMade += 1;
    }

    if (expectedPayments !== null && !settled && paymentsMade >= expectedPayments) {
      settled = true;
      if (actual < cap - MONEY_EPSILON) {
        addToDay(returnedByDay, event.date, cap - actual);
        cap = actual;
      }
    }

    const over = Math.max(0, actual - cap);
    const delta = over - countedOver;
    if (delta > MONEY_EPSILON) {
      addToDay(spentByDay, event.date, delta);
    } else if (delta < -MONEY_EPSILON) {
      addToDay(returnedByDay, event.date, -delta);
    }
    countedOver = over;
  });

  return { actual, paymentsMade, settled, events: sortedEvents };
};

/**
 * Splits the free money into weeks proportionally to their days. Results of closed weeks are carried
 * over to all remaining weeks; open weeks are expected to use at least their budget, so only
 * overspending is reflected in the following weeks before the week ends.
 */
export const buildPlanWeeks = (
  period: PlanPeriod,
  freeAmount: number,
  spentByDay: DailyAmounts,
  returnedByDay: DailyAmounts,
  todayKey: string
): PlanWeek[] => {
  const monthWeeks = getMonthWeeks(period.year, period.month);
  const totalDays = sum(monthWeeks.map(week => week.days));
  let remaining = freeAmount;
  let remainingDays = totalDays;

  return monthWeeks.map(week => {
    const status: WeekStatus = week.endKey < todayKey ? 'past' : week.startKey > todayKey ? 'future' : 'current';
    const spent = sumWeek(spentByDay, week);
    const returned = sumWeek(returnedByDay, week);
    const net = spent - returned;
    const baseBudget = totalDays > 0 ? freeAmount * week.days / totalDays : 0;
    const budget = remainingDays > 0 ? remaining * week.days / remainingDays : 0;

    remaining -= status === 'past' ? net : Math.max(budget, net);
    remainingDays -= week.days;

    const daysLeft = status === 'past'
      ? 0
      : status === 'future'
        ? week.days
        : week.endDay - Number(todayKey.slice(8, 10)) + 1;

    return {
      ...week,
      status,
      baseBudget: roundMoney(baseBudget),
      budget: roundMoney(budget),
      carryOver: roundMoney(budget - baseBudget),
      spent: roundMoney(spent),
      returned: roundMoney(returned),
      left: roundMoney(budget - net),
      daysLeft
    };
  });
};

/**
 * Evaluates the plan against the actual month activity:
 *  - template receipts are payments of the matching template items;
 *  - expenses of a custom item category are payments of that item;
 *  - everything else (plus anything above the plan) is spent from the weekly budget.
 */
export const computeMonthlyPlan = (
  plan: PlanInput,
  activity: MonthlyPlanActivity | null | undefined,
  todayKey: string
): MonthlyPlanSummary => {
  const totalIncome = sum(plan.incomes.map(income => income.amount));
  const totalFixed = sum(plan.fixedExpenses.map(item => item.amount));
  const totalSavings = sum(plan.savingsGoals.map(goal => goal.amount));
  const freeAmount = totalIncome - totalFixed - totalSavings;

  const spentByDay: DailyAmounts = new Map();
  const returnedByDay: DailyAmounts = new Map();

  const templateItems = new Map<string, PlanFixedExpenseInput>();
  const categoryItems = new Map<string, PlanFixedExpenseInput>();
  plan.fixedExpenses.forEach(item => {
    if (item.recurringExpenseId) {
      if (!templateItems.has(item.recurringExpenseId)) {
        templateItems.set(item.recurringExpenseId, item);
      }
    } else if (item.categoryId && !categoryItems.has(item.categoryId)) {
      categoryItems.set(item.categoryId, item);
    }
  });

  const itemEvents = new Map<string, ItemEvent[]>();
  const pushEvent = (key: string, event: ItemEvent): void => {
    itemEvents.set(key, [...(itemEvents.get(key) ?? []), event]);
  };

  (activity?.templatePayments ?? []).forEach(payment => {
    const date = toDateKey(payment.date);
    const item = templateItems.get(payment.recurringExpenseId);

    if (item) {
      pushEvent(item.key, {
        date,
        amount: payment.amount,
        isPayment: true,
        receiptId: payment.receiptId,
        receiptName: payment.receiptName
      });
    } else if (!payment.isSavingsAccount) {
      addToDay(spentByDay, date, payment.amount);
    }
  });

  (activity?.expenses ?? []).forEach(expense => {
    const date = toDateKey(expense.date);
    const item = categoryItems.get(expense.categoryId);

    if (item) {
      pushEvent(item.key, { date, amount: expense.amount, isPayment: false });
    } else {
      addToDay(spentByDay, date, expense.amount);
    }
  });

  const daysInMonth = getDaysInMonth(plan.year, plan.month);
  const monthEndKey = makeDateKey(plan.year, plan.month, daysInMonth);

  const fixedExpenses: FixedExpenseProgress[] = plan.fixedExpenses.map(item => {
    const isTemplateItem = !!item.recurringExpenseId;
    const expectedPayments = isTemplateItem ? Math.max(1, item.paymentsCount) : null;
    const result = settleItem(item.amount, expectedPayments, itemEvents.get(item.key) ?? [], spentByDay, returnedByDay);
    const isPaid = result.settled || result.actual >= item.amount - MONEY_EPSILON;
    const dueDateKey = item.dayOfMonth
      ? makeDateKey(plan.year, plan.month, Math.min(item.dayOfMonth, daysInMonth))
      : isTemplateItem ? monthEndKey : null;

    let status: FixedExpenseStatus = isPaid ? 'paid' : result.actual > MONEY_EPSILON ? 'partial' : 'pending';
    if (status !== 'paid' && isTemplateItem && dueDateKey !== null && dueDateKey < todayKey) {
      status = 'overdue';
    }

    return {
      key: item.key,
      planned: item.amount,
      actual: roundMoney(result.actual),
      paymentsMade: isTemplateItem ? result.paymentsMade : null,
      expectedPayments,
      status,
      overAmount: roundMoney(Math.max(0, result.actual - item.amount)),
      dueDateKey,
      isTracked: isTemplateItem || !!item.categoryId,
      payments: result.events.map(({ isPayment, ...payment }) => payment)
    };
  });

  const transfersByAccount = new Map<string, ItemEvent[]>();
  (activity?.savingsTransfers ?? []).forEach(transfer => {
    transfersByAccount.set(transfer.accountId, [
      ...(transfersByAccount.get(transfer.accountId) ?? []),
      { date: toDateKey(transfer.date), amount: transfer.amount, isPayment: false }
    ]);
  });

  const goalAccounts = new Set<string>();
  const savingsGoals: SavingsGoalProgress[] = plan.savingsGoals.map(goal => {
    const events = goalAccounts.has(goal.accountId) ? [] : transfersByAccount.get(goal.accountId) ?? [];
    goalAccounts.add(goal.accountId);

    const { actual } = settleItem(goal.amount, null, events, spentByDay, returnedByDay);

    return {
      key: goal.key,
      planned: goal.amount,
      actual: roundMoney(actual),
      remaining: roundMoney(Math.max(0, goal.amount - actual)),
      progress: goal.amount > 0 ? (actual / goal.amount) * 100 : 0
    };
  });

  // Savings without a goal in the plan still take money out of the monthly budget
  let unplannedSavings = 0;
  transfersByAccount.forEach((events, accountId) => {
    if (!goalAccounts.has(accountId)) {
      const { actual } = settleItem(0, null, events, spentByDay, returnedByDay);
      unplannedSavings += Math.max(0, actual);
    }
  });

  const weeks = buildPlanWeeks(plan, freeAmount, spentByDay, returnedByDay, todayKey);
  const variableSpent = sum(weeks.map(week => week.spent));
  const returned = sum(weeks.map(week => week.returned));

  return {
    totalIncome: roundMoney(totalIncome),
    totalFixed: roundMoney(totalFixed),
    totalSavings: roundMoney(totalSavings),
    freeAmount: roundMoney(freeAmount),
    actualIncome: roundMoney(activity?.totalIncome ?? 0),
    fixedActual: roundMoney(sum(fixedExpenses.map(item => item.actual))),
    savingsActual: roundMoney(sum(savingsGoals.map(goal => goal.actual))),
    unplannedSavings: roundMoney(unplannedSavings),
    variableSpent: roundMoney(variableSpent),
    returned: roundMoney(returned),
    freeLeft: roundMoney(freeAmount - variableSpent + returned),
    fixedExpenses,
    savingsGoals,
    weeks,
    currentWeek: weeks.find(week => week.status === 'current') ?? null
  };
};
